/** Frontend-ACL – spiegelt `api/acl.py` PAGE_ACL (ohne separaten /acl.json-Endpunkt). */

function withOffice(groups: string[]): string[] {
  return groups.includes("Camping-Buchhaltung") ? [...groups, "Büro"] : groups;
}

const PAGE_ACL: Record<string, string[]> = {
  "/": ["*"],
  "/kein-zugang": ["*"],
  "/guests": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/contracts": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/pitches": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/stammdaten": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/bookings": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/invoices": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/cashbook": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/daily-closing": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/reports": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/arrivals": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/departures": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/gaeste-am-platz": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/strom": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/platzplan": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/planner": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/audit": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/pos": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung"]),
  "/mail": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
  "/tickets": withOffice(["Camping-Admin", "Camping-Rezeption", "Camping-Buchhaltung", "Camping-Lesen"]),
};


export const ADMIN_GROUPS = ["Camping-Admin", "App-Admins", "App-Admin", "Admins"];

export function normGroups(arr: string[] = []): string[] {
  return (arr || [])
    .map((s) => {
      if (typeof s !== "string") return s;
      const m = /^CN=([^,]+)/i.exec(s);
      return m?.[1] ? m[1] : s;
    })
    .filter((s): s is string => typeof s === "string");
}

/** Entspricht api/auth.py `_canonical_group_name` (ohne Unicode-Zerlegung). */
export function canonicalGroupName(raw: string): string {
  return raw
    .trim()
    .toLocaleLowerCase("de")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
}

function compactGroupName(raw: string): string {
  return canonicalGroupName(raw).replace(/[\s_-]+/g, "");
}

/** Gruppe App-Nutzer – erforderlich für die Anmeldung an der App. */
export function isAppUser(userGroups: string[] = []): boolean {
  return normGroups(userGroups).some((group) => compactGroupName(group) === "appnutzer");
}

/** Gruppe Büro – Zugriff auf buchhaltungsrelevante Daten. */
export function isOfficeMember(userGroups: string[] = []): boolean {
  return normGroups(userGroups).some((group) => compactGroupName(group) === "buero");
}

export function normalizeAclPath(p: string = "/"): string {
  let x = (p || "/").trim().toLowerCase();
  const queryIdx = x.indexOf("?");
  if (queryIdx >= 0) x = x.slice(0, queryIdx);
  const hashIdx = x.indexOf("#");
  if (hashIdx >= 0) x = x.slice(0, hashIdx);
  if (!x.startsWith("/")) x = `/${x}`;
  return x.replace(/\/+$/, "") || "/";
}


export function isAllowed(userGroups: string[] = [], rule: string[] | undefined): boolean {
  if (!rule || rule.length === 0) return false;
  if (rule.includes("*")) return true;

  const userKeys = new Set(normGroups(userGroups).map(canonicalGroupName));
  const adminKeys = new Set(ADMIN_GROUPS.map(canonicalGroupName));
  if ([...userKeys].some((key) => adminKeys.has(key))) return true;

  const ruleKeys = new Set(rule.map(canonicalGroupName));
  return [...userKeys].some((key) => ruleKeys.has(key));
}

export function aclRuleForPath(path: string): string[] | undefined {
  if (PAGE_ACL[path]) return PAGE_ACL[path];
  const segments = path.split("/").filter(Boolean);
  for (let i = segments.length; i > 0; i -= 1) {
    const parent = `/${segments.slice(0, i).join("/")}`;
    if (PAGE_ACL[parent]) return PAGE_ACL[parent];
  }
  return undefined;
}

export async function canViewHref(href: string, userGroups: string[]): Promise<boolean> {
  const path = normalizeAclPath(href);
  const rule = aclRuleForPath(path);
  return isAllowed(userGroups, rule);
}
